import { ASSIGN_PLAN_TTL_MS, AssignPlan, isAssignPlanSettled } from "@edu/shared";

/**
 * План раздачи в `localStorage` (5.7 docs/homework-phase2-design.md).
 *
 * Зачем вообще: вкладку на телефоне убивает операционная система, и это штатный
 * сценарий, а не экзотика. Преподаватель раздаёт двенадцати, после пятого ответа
 * уходит в мессенджер, возвращается, отмечает тех же двенадцать и жмёт. Если
 * ключи идемпотентности жили только в состоянии React, у первых пяти появляется
 * по второй работе, а с генераторами она ещё и с другими числами, то есть не
 * дубль, а второе задание.
 *
 * Прецедент в проекте тот же и ровно про это: черновики ответов ученика живут
 * здесь же (`../drafts.ts`), потому что вкладка на телефоне не считается
 * надёжным хранилищем.
 *
 * ⚠️ Каждый `setItem` в try/catch, и отказ квоты это ВИДИМОЕ состояние: Safari в
 * приватном режиме бросает `QuotaExceededError` на первой же записи. Тогда план
 * живёт в памяти вкладки, а форма честно пишет, что закрывать её нельзя.
 */

const PREFIX = "hwplan:";

function keyOf(planRunId: string): string {
  return `${PREFIX}${planRunId}`;
}

/** `false` означает «хранилище не пишет»: план остаётся в памяти вкладки. */
export function savePlan(plan: AssignPlan): boolean {
  try {
    localStorage.setItem(keyOf(plan.planRunId), JSON.stringify(plan));
    return true;
  } catch {
    return false;
  }
}

export function dropPlan(planRunId: string): void {
  try {
    localStorage.removeItem(keyOf(planRunId));
  } catch {
    /* хранилище не пишет: удалять тоже нечего */
  }
}

export interface PlanSweep {
  /** Незаконченные, свежие сверху. Форма предлагает продолжить первый. */
  pending: AssignPlan[];
  /** Удалённые по возрасту: о них форма обязана сказать вслух, а не молча. */
  expired: number;
}

/**
 * Уборка при каждом открытии формы.
 *
 * ⚠️ Удаляется план по признаку «все обслужены», а не по возрасту, и живёт до 30
 * суток. Наоборот было бы хуже: сценарий, ради которого план заведён, регулярно
 * означает возврат через день или два, план к тому времени был бы удалён, и
 * преподаватель отметил бы тех же двенадцать заново, получив ровно тот дубль, от
 * которого защищались. Цена лишнего плана это несколько килобайт, цена
 * удалённого это вторая работа.
 *
 * Неразобравшийся план удаляется молча: это запись другого бандла, спасать в ней
 * нечего, а отказ разбора не должен уносить форму.
 */
export function sweepPlans(now: number): PlanSweep {
  const pending: AssignPlan[] = [];
  let expired = 0;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null || !key.startsWith(PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (raw === null) continue;

      let parsed: ReturnType<typeof AssignPlan.safeParse>;
      try {
        parsed = AssignPlan.safeParse(JSON.parse(raw));
      } catch {
        doomed.push(key);
        continue;
      }
      if (!parsed.success) {
        doomed.push(key);
        continue;
      }
      const plan = parsed.data;
      if (isAssignPlanSettled(plan)) {
        doomed.push(key);
        continue;
      }
      if (now - plan.createdAt > ASSIGN_PLAN_TTL_MS) {
        doomed.push(key);
        expired += 1;
        continue;
      }
      pending.push(plan);
    }
    for (const key of doomed) localStorage.removeItem(key);
  } catch {
    /* приватный режим: читать нечего, писать туда тоже не выходило */
  }
  pending.sort((a, b) => b.createdAt - a.createdAt);
  return { pending, expired };
}
